import { useEffect, useRef } from "react";
import { useTheme } from "@/context/ThemeContext";

export default function AnimatedBackground() {
  const canvasRef = useRef(null);
  const { theme } = useTheme();
  const themeRef = useRef(theme);

  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let time = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Cube vertices (unit cube centered at origin)
    const cubeVerts = [
      [-1,-1,-1],[1,-1,-1],[1,1,-1],[-1,1,-1],
      [-1,-1,1],[1,-1,1],[1,1,1],[-1,1,1],
    ];
    const cubeEdges = [
      [0,1],[1,2],[2,3],[3,0],
      [4,5],[5,6],[6,7],[7,4],
      [0,4],[1,5],[2,6],[3,7],
    ];

    function rotateY(v, a) {
      const c = Math.cos(a), s = Math.sin(a);
      return [v[0]*c + v[2]*s, v[1], -v[0]*s + v[2]*c];
    }
    function rotateX(v, a) {
      const c = Math.cos(a), s = Math.sin(a);
      return [v[0], v[1]*c - v[2]*s, v[1]*s + v[2]*c];
    }
    function project(v, cx, cy, scale, fov) {
      const z = v[2] + fov;
      const f = fov / z;
      return [cx + v[0] * scale * f, cy + v[1] * scale * f, z];
    }

    // Ribbon wave parameters
    const ribbons = [
      { yOff: 0.3, amp: 40, freq: 0.008, speed: 0.4, width: 1.5 },
      { yOff: 0.55, amp: 30, freq: 0.006, speed: 0.3, width: 1 },
      { yOff: 0.75, amp: 50, freq: 0.01, speed: 0.5, width: 1.2 },
    ];

    // Floating particles
    const particles = Array.from({ length: 20 }, () => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0002,
      vy: (Math.random() - 0.5) * 0.0002,
      size: Math.random() * 2 + 0.5,
      pulse: Math.random() * Math.PI * 2,
    }));

    function draw() {
      const w = canvas.width;
      const h = canvas.height;
      const isDark = themeRef.current === "dark";
      ctx.clearRect(0, 0, w, h);

      const goldBase = isDark ? [245, 166, 35] : [184, 132, 26];
      const copperBase = isDark ? [200, 115, 67] : [160, 93, 48];

      // ─── Draw Ribbons ───
      ribbons.forEach((r, ri) => {
        const baseY = h * r.yOff;
        ctx.beginPath();
        for (let x = 0; x <= w; x += 3) {
          const y = baseY + Math.sin(x * r.freq + time * r.speed) * r.amp
                          + Math.sin(x * r.freq * 0.5 + time * r.speed * 0.7) * r.amp * 0.4;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        const c = ri % 2 === 0 ? goldBase : copperBase;
        const alpha = isDark ? 0.12 : 0.09;
        ctx.strokeStyle = `rgba(${c[0]},${c[1]},${c[2]},${alpha})`;
        ctx.lineWidth = r.width;
        ctx.stroke();

        // Second parallel line for ribbon thickness
        ctx.beginPath();
        for (let x = 0; x <= w; x += 3) {
          const y = baseY + Math.sin(x * r.freq + time * r.speed) * r.amp
                          + Math.sin(x * r.freq * 0.5 + time * r.speed * 0.7) * r.amp * 0.4 + 15;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(${c[0]},${c[1]},${c[2]},${alpha * 0.6})`;
        ctx.lineWidth = r.width * 0.6;
        ctx.stroke();
      });

      // ─── Draw Rotating Cube ───
      const cubeScale = Math.min(w, h) * 0.12;
      const cubeCx = w * 0.82;
      const cubeCy = h * 0.35;
      const ry = time * 0.15;
      const rx = time * 0.1;

      const projected = cubeVerts.map(v => {
        let r1 = rotateY(v, ry);
        let r2 = rotateX(r1, rx);
        return project(r2, cubeCx, cubeCy, cubeScale, 5);
      });

      cubeEdges.forEach(([a, b]) => {
        const pa = projected[a];
        const pb = projected[b];
        const avgZ = (pa[2] + pb[2]) / 2;
        const alpha = isDark
          ? Math.max(0.06, Math.min(0.2, 0.12 + (avgZ - 4) * 0.03))
          : Math.max(0.04, Math.min(0.14, 0.09 + (avgZ - 4) * 0.02));
        ctx.beginPath();
        ctx.moveTo(pa[0], pa[1]);
        ctx.lineTo(pb[0], pb[1]);
        ctx.strokeStyle = `rgba(${goldBase[0]},${goldBase[1]},${goldBase[2]},${alpha})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      });

      // Cube vertices as dots
      projected.forEach(p => {
        const alpha = isDark ? 0.3 : 0.18;
        ctx.beginPath();
        ctx.arc(p[0], p[1], 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${goldBase[0]},${goldBase[1]},${goldBase[2]},${alpha})`;
        ctx.fill();
      });

      // ─── Second smaller cube (bottom-left) ───
      const cube2Scale = Math.min(w, h) * 0.06;
      const cube2Cx = w * 0.12;
      const cube2Cy = h * 0.7;
      const ry2 = -time * 0.12;
      const rx2 = time * 0.08 + 1;

      const projected2 = cubeVerts.map(v => {
        let r1 = rotateY(v, ry2);
        let r2 = rotateX(r1, rx2);
        return project(r2, cube2Cx, cube2Cy, cube2Scale, 5);
      });

      cubeEdges.forEach(([a, b]) => {
        const pa = projected2[a];
        const pb = projected2[b];
        const alpha = isDark ? 0.1 : 0.06;
        ctx.beginPath();
        ctx.moveTo(pa[0], pa[1]);
        ctx.lineTo(pb[0], pb[1]);
        ctx.strokeStyle = `rgba(${copperBase[0]},${copperBase[1]},${copperBase[2]},${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      // ─── Floating Particles ───
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > 1) p.vx *= -1;
        if (p.y < 0 || p.y > 1) p.vy *= -1;
        p.pulse += 0.02;
        const alpha = (isDark ? 0.25 : 0.15) * (0.5 + 0.5 * Math.sin(p.pulse));
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${goldBase[0]},${goldBase[1]},${goldBase[2]},${alpha})`;
        ctx.fill();
      });

      time += 0.016;
      animId = requestAnimationFrame(draw);
    }

    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="animated-background"
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
}
